/**
 * 429 (Too Many Requests) backoff for Google user icon (profile image) URL fetches only.
 * When a fetch of a Google profile photo URL returns 429, we record it and avoid fetching
 * Google user icon URLs again for 24 hours. Does not apply to Firebase Auth or Firestore.
 */

const STORAGE_KEY = 'gradedplay_google_429_backoff_until';
const BACKOFF_MS = 24 * 60 * 60 * 1000; // 24 hours

function getBackoffUntil() {
  if (typeof window === 'undefined' || !window.localStorage) return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const ts = parseInt(raw, 10);
    return isNaN(ts) ? null : ts;
  } catch {
    return null;
  }
}

function setBackoffUntil(ts) {
  if (typeof window === 'undefined' || !window.localStorage) return;
  try {
    localStorage.setItem(STORAGE_KEY, String(ts));
  } catch (_) {}
}

/**
 * Returns true if the error is a 429 / rate limit from Google (Firebase Auth, Firestore, or HTTP).
 */
export function is429Error(error) {
  if (!error) return false;
  const code = (error.code || '').toString();
  const message = (error.message || '').toString();
  // Firestore
  if (code === 'resource-exhausted') return true;
  // Firebase Auth
  if (code === 'auth/too-many-requests') return true;
  // HTTP
  if (error.httpStatus === 429 || error.status === 429) return true;
  if (message.includes('429') || message.toLowerCase().includes('too many requests')) return true;
  return false;
}

/**
 * Returns true if we are still in the 24-hour backoff (do not call Google user content).
 */
export function isInBackoff() {
  const until = getBackoffUntil();
  if (!until) return false;
  return Date.now() < until;
}

/**
 * Call this when you receive a 429. Sets backoff until now + 24 hours.
 */
export function record429() {
  const until = Date.now() + BACKOFF_MS;
  setBackoffUntil(until);
}

/**
 * Returns milliseconds remaining in the current backoff, or 0 if not in backoff.
 */
export function getBackoffRemainingMs() {
  const until = getBackoffUntil();
  if (!until) return 0;
  const remaining = until - Date.now();
  return remaining > 0 ? remaining : 0;
}

/**
 * Throws a user-friendly error if we are in backoff. Call before making a Google user content request.
 */
export function assertNotInBackoff() {
  if (!isInBackoff()) return;
  const ms = getBackoffRemainingMs();
  const hours = Math.ceil(ms / (60 * 60 * 1000));
  throw new Error(
    `Rate limited by Google. Please try again in about ${hours} hour(s).`
  );
}
